import { CtResponse } from "../Fetch";
import { xget, xhttp } from "../Fetchv2";
import { PGSchema } from "../schemas/pg.schema.js";

export class Users {
	echo() {
		return xget<{ headers: Record<string, string>; query: Record<string, string> }>("/echo", {}, { noauth: true });
	}
	deleteAccount(email: string) {
		return xhttp<{ ok: boolean }>(`/users/me/delete`, "DELETE", { email });
	}

	changeEmail(email: string) {
		return xhttp<{ token: string }>(`/users/me/email`, "PUT", { email });
	}

	changePassword(password: string) {
		return xhttp<{ token: string }>(`/users/me/password`, "PUT", { password });
	}

	createUserWithGoogle(token: string) {
		return xhttp<{ uid: string; created: boolean; email: string; pass: string }>(`/users/google`, "POST", { token: token, referrer: document.referrer, type: "google" });
	}
	createUserWithGoogleYolo(token: string): Promise<CtResponse<{ uid: string; created: boolean; email: string; pass: string }>> {
		return xhttp(`/users/googleyolo`, "POST", { token: token, referrer: document.referrer, type: "googleyolo" });
	}
	createUserWithMicrosoft(token: string): Promise<CtResponse<{ uid: string; created: boolean; email: string; pass: string }>> {
		return xhttp(`/users/microsoft`, "POST", { token: token, referrer: document.referrer });
	}
	createUserWithFacebook(token: string) {
		return xhttp<{ uid: string; created: boolean; email: string; pass: string }>(`/users/facebook`, "POST", { token: token, referrer: document.referrer });
	}

	createUser(
		user: Pick<PGSchema.users, "name" | "last_name" | "email" | "wa_phone" | "gender" | "birthday" | "dpi" | "pp" | "thumb_pp" | "notifications_unsub" | "referrer" | "user_lang" | "signup">,
		password?: string
	) {
		const body = password ? { ...user, pass: password } : user;
		return xhttp<{ uid: string; created: boolean; email: string; reason?: string }, { created: boolean; reason: string }>(`/users`, "POST", body);
	}

	createUserFromPage(
		user: Pick<PGSchema.users, "name" | "last_name" | "email" | "wa_phone" | "gender" | "birthday" | "dpi" | "pp" | "thumb_pp" | "notifications_unsub" | "referrer" | "user_lang">,
		token: string,
		page_id?: string
	) {
		return xhttp<{ uid: string; created: boolean; email: string; reason?: string }, { created: boolean; reason: string }>(`/pages/${page_id}/users`, "POST", user, {
			headers: {
				"app-token": token
			}
		});
	}

	createUserFromPageJobPosting(
		user: Pick<PGSchema.users, "name" | "last_name" | "email" | "wa_phone" | "gender" | "birthday" | "dpi" | "pp" | "thumb_pp" | "notifications_unsub" | "referrer" | "user_lang">,
		token: string,
		page_id: string,
		job_data: {
			co_id: PGSchema.jobs["co_id"];
			job_id: PGSchema.jobs["job_id"];
			geo?: PGSchema.jobs["geo"];
			referrer: string;
			htm_source?: string | null;
			htm_medium?: string | null;
			htm_campaign?: string | null;
			htm_term?: string | null;
			htm_content?: string | null;
		},
		phase: PGSchema.users_jobs["phase"],
		module: "jobs" | "jobboard" | "headhunting"
	) {
		/**
		 * Tipos de error en `data.type`:
		 * 1 = Email requerido o formato inválido
		 * 2 = El candidato ya aplicó a esta posición
		 * 3 = Trabajo no encontrado
		 * 4 = Página no encontrada
		 * 5 = No se pudo guardar la información del candidato (error interno)
		 */
		return xhttp<
			{ uid: string; created: boolean; email: string; reason?: string; id: number; co_id: string; job_id: string; msg?: string; assigned_tests: number[]; phase?: number },
			{ type?: 1 | 2 | 3 | 4 | 5 }
		>(
			`/pages/${page_id}/users/job-posting`,
			"POST",
			{ body: user, job_data: job_data, phase: phase, module: module },
			{
				headers: {
					"app-token": token
				}
			}
		);
	}

	getCustomToken(user: { token?: string; email?: string }) {
		return xhttp<{ token: string }>(`/users/token`, "POST", user);
	}
	verifyIfExists(user: { email?: PGSchema.users["email"]; phone?: PGSchema.users["wa_phone"]; dpi?: PGSchema.users["dpi"] }) {
		return xget<{
			/**
			 * Existe el usuario?
			 */
			exists: boolean;
			/**
			 * Tiene paginas asignadas?
			 */
			hp: boolean;
			token?: string;
			email_masked?: string;
		}>(`/users/exists`, user);
	}

	checkIfDPIUsedByOtherUser(dpi: string, email: string) {
		return xget<{
			match: boolean;
			email_masked?: string;
		}>(`/users/check-dpi`, { dpi, email });
	}

	/** Obtine el custom Token y guarda el Facebook ID */
	getCustomTokenWithFacebook(body: { access_token: string; fb_id: string }) {
		return xhttp<{ token: string }>(`/users/token/with/facebook`, "POST", body);
	}
	/** Obtine el custom Token y guarda el Facebook ID */
	getCustomTokenWithGoogle(body: { access_token: string; gid?: string }) {
		return xhttp<{ token: string }>(`/users/token/with/google`, "POST", body);
	}
	/** Obtine el custom Token y guarda el Facebook ID */
	getCustomTokenWithGoogleYolo(body: { access_token: string }) {
		return xhttp<{ token: string }>(`/users/token/with/googleyolo`, "POST", body);
	}
	/** Obtine el custom Token y guarda el Facebook ID */
	getCustomTokenWithMicrosoft(body: { access_token: string; uid?: string }) {
		return xhttp<{ token: string }>(`/users/token/with/microsoft`, "POST", body);
	}

	/** Obtine el custom Token y guarda el Facebook ID */
	getCustomTokenWithWhatsapp(body: { wa_phone: string; code: number }) {
		return xhttp<{ token: string }>(`/users/token/with/whatsapp`, "POST", body);
	}

	/** Obtine el custom Token y guarda el Facebook ID */
	generateOTPWithWhatsapp(body: { wa_phone: string; code: number }) {
		return xhttp<{}>(`/users/token/with/whatsapp`, "PUT", body);
	}

	/** Envia un email de verificación al usuario con un OTP */
	getCustomTokenWithEmail(body: { email: string; force_send?: boolean; long?: boolean }) {
		return xhttp<{ ok: boolean }>(`/users/token/with/email`, "POST", body);
	}

	verifyCustomTokenWithEmail(body: { email: string; code: number; anonymous?: boolean }) {
		return xhttp<{ token: string }>(`/users/token/with/email`, "PUT", body);
	}

	discoverPeople(list: { name: string; id: string }[]) {
		return xhttp(`/users/me/discover-people`, "POST", { list: list });
	}

	/**
	 * @GET /users/:uid
	 */
	getUserInformation(_id: string, headers?: Record<string, string>) {
		headers ||= {};
		return xget<{ user: PGSchema.users; pages: PGSchema.pages[] }>(`/users/${_id}`, {}, { headers });
	}

	getUserInformationByToken(token: string) {
		return xget<{ user: PGSchema.users; pages: PGSchema.pages[] }>(`/public/users`, { token });
	}

	updateInfo(info: Partial<PGSchema.users>, uid = "me", token?: string, options?: { /** Only Mongo */ om?: boolean }) {
		return xhttp<{ update: boolean }, { field: string; reason: string }>(`/users/${uid}`, "PUT", { info, token, options });
	}

	editProfileTemp(info: any) {
		return xhttp<{ update: boolean }>(`/users_temp/me`, "PUT", { info });
	}

	async getUserStatus(email?: string) {
		interface Status {
			msg: number;
			tests: number;
			notif: number;
			token?: string;
		}
		return xhttp<Status>(`/users/me/status`, "POST", { control: "user_stats", time: 30, page: location.pathname });
	}

	async search(q?: string, onlyPages = false, pageId?: string, token?: string) {
		return xget<{
			data: Pick<PGSchema.users, "id" | "name" | "last_name" | "title" | "thumb_pp" | "is_verified" | "username" | "insignia">[];
		}>(`/search/users`, { q, onlyPages, page: pageId }, { headers: { ...(token ? { "app-token": token } : {}) } });
	}

	async updateUserRecruiter(pgid: string, uid: string, usersData: Partial<PGSchema.users>) {
		return xhttp<{ user: PGSchema.users }>(`/pages/${pgid}/users/${uid}`, "PUT", usersData);
	}
}
